import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";

/* eslint-disable hubspot-dev/no-private-classes */
import { CALYPSO, GYPSUM, OZ, PANTERA } from 'HubStyleTokens/colors';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { Fragment, useState } from 'react';
import styled from 'styled-components';
import partial from 'transmute/partial';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIModal from 'UIComponents/dialog/UIModal';
import H3 from 'UIComponents/elements/headings/H3';
import UISelect from 'UIComponents/input/UISelect';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import { useAppSelector } from '../../../../reducers';
import { calculatedPriceSelector } from '../../../../selectors/PricingSelectors';
import { suiteProductConfigurationsSelector } from '../../../../selectors/ProductConfigsSelectors';
import { crmSuiteProductLevelsSelector, crmSuiteProductLinesForProductLevelSelector } from '../../../../selectors/ProductsSelectors';
import PriceDisplay from '../../../common/pricing/PriceDisplay';
import FooterMicrocopy from '../../../faqs/FooterMicrocopy';
import MobileSuiteCatalogEditionHeader from './MobileSuiteCatalogEditionHeader';
import MobileSuiteCatalogFeaturesTable from './MobileSuiteCatalogFeaturesTable';
const MODAL_WIDTH = 600;
const HEADER_HEIGHT = 128;
const MODAL_TOP_MARGIN = 60;
const MODAL_BOTTOM_MARGIN = 47;
const ModalHeader = styled.header.withConfig({
  displayName: "MobileSuiteCatalog__ModalHeader",
  componentId: "sc-1ewkdgo-0"
})(["text-align:center;z-index:100;background-color:", ";padding:15px 0;"], GYPSUM);
const PricingContainer = styled.div.withConfig({
  displayName: "MobileSuiteCatalog__PricingContainer",
  componentId: "sc-1ewkdgo-1"
})(["margin:0 auto;line-height:normal;"]);
const Price = styled.div.withConfig({
  displayName: "MobileSuiteCatalog__Price",
  componentId: "sc-1ewkdgo-2"
})(["font-size:20px;color:", ";margin-right:8px;font-weight:600;display:inline-block;"], props => props.isDiscounted ? OZ : PANTERA);
const CrossedOutPrice = styled(PriceDisplay).withConfig({
  displayName: "MobileSuiteCatalog__CrossedOutPrice",
  componentId: "sc-1ewkdgo-3"
})(["color:", ";font-size:16px;line-height:27px;text-decoration:line-through;font-weight:500;"], PANTERA);
const CrossOutPerMonth = styled.span.withConfig({
  displayName: "MobileSuiteCatalog__CrossOutPerMonth",
  componentId: "sc-1ewkdgo-4"
})(["font-size:16px;line-height:27px;text-decoration:line-through;font-weight:500;"]);
const StyledSelect = styled(UISelect).withConfig({
  displayName: "MobileSuiteCatalog__StyledSelect",
  componentId: "sc-1ewkdgo-5"
})(["width:fit-content;margin:8px auto 16px;font-size:26px;.private-dropdown__caret{font-size:20px;color:", ";margin-left:8px;}.crm-suite-bold{font-weight:700;}"], CALYPSO);

const MobileSuiteCatalogModal = ({
  initialProductLevel,
  queryParamTerm,
  currencyCode,
  onCloseClick
}) => {
  const [activeProductLevel, setActiveProductLevel] = useState(initialProductLevel);

  const getOption = productLevel => {
    const text = /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: `pricingPage.pages.suite.catalogModal.${productLevel.toLowerCase()}`
    });

    return {
      dropdownText: text,
      buttonText: text,
      value: productLevel
    };
  };

  const pricingInfo = useAppSelector(state => {
    const productConfigurations = suiteProductConfigurationsSelector(state, {
      productLevel: activeProductLevel
    });
    return calculatedPriceSelector(state, {
      productConfigurations,
      queryParamTerm: queryParamTerm || undefined
    });
  });
  const isDiscounted = pricingInfo.monthly.calculatedPrice < pricingInfo.monthly.fullPrice;

  const handleProductLevelChange = evt => {
    getUsageTracker().track('modalInteraction', {
      action: 'changed product level',
      modalName: 'suite-catalog-modal',
      productLevel: evt.currentTarget.value
    });
    setActiveProductLevel(evt.currentTarget.value);
  };

  const suiteProductLevels = useAppSelector(crmSuiteProductLevelsSelector);

  const renderModalHeader = () => {
    return /*#__PURE__*/_jsxs(ModalHeader, {
      children: [/*#__PURE__*/_jsx(H3, {
        className: "display-inline",
        children: /*#__PURE__*/_jsx(StyledSelect, {
          buttonUse: "unstyled",
          onChange: handleProductLevelChange,
          options: suiteProductLevels.map(productLevel => getOption(productLevel)),
          value: activeProductLevel,
          searchable: false,
          menuWidth: "auto"
        })
      }), /*#__PURE__*/_jsxs(PricingContainer, {
        children: [/*#__PURE__*/_jsx(Price, {
          isDiscounted: isDiscounted,
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "planBuilder.priceDisplay.pricePerMonth",
            options: {
              price: /*#__PURE__*/_jsx(PriceDisplay, {
                price: pricingInfo.monthly.calculatedPrice,
                currencyCode: currencyCode
              })
            }
          })
        }), isDiscounted && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(CrossedOutPrice, {
            price: pricingInfo.monthly.fullPrice,
            currencyCode: currencyCode
          }), /*#__PURE__*/_jsx(CrossOutPerMonth, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "planBuilder.priceDisplay.perMonth"
            })
          })]
        })]
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: partial(onCloseClick)
      })]
    });
  };

  const renderGrowthHubEditionTable = productLine => {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(MobileSuiteCatalogEditionHeader, {
        productLevel: activeProductLevel,
        productLine: productLine,
        queryParamTerm: queryParamTerm
      }), /*#__PURE__*/_jsx(MobileSuiteCatalogFeaturesTable, {
        productLevel: activeProductLevel,
        productLine: productLine
      })]
    }, productLine);
  };

  const hubsInCrmSuiteTier = useAppSelector(state => crmSuiteProductLinesForProductLevelSelector(state, {
    productLevel: activeProductLevel
  }));
  return /*#__PURE__*/_jsxs(UIModal, {
    use: "conversational",
    width: MODAL_WIDTH,
    children: [renderModalHeader(), /*#__PURE__*/_jsxs(UIScrollContainer, {
      maxHeight: `calc(100vh - ${HEADER_HEIGHT + MODAL_TOP_MARGIN + MODAL_BOTTOM_MARGIN}px)`,
      scrollDirection: 'vertical',
      children: [hubsInCrmSuiteTier.map(productLine => {
        return renderGrowthHubEditionTable(productLine);
      }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
    })]
  });
};

export default MobileSuiteCatalogModal;